// Home page — new 4-pillar narrative anchored around:
// MAPS → Je vois · ACADEMY → J'apprends · AI → Je comprends · TASTE → Je déguste · IFRAME → J'intègre
//
// Above the fold: AnimatedHero (unchanged). Below: PillarsSchema →
// AppExperienceBlock (AI = the app, 5 usages) → Maps → Academy → Audiences →
// Iframe (B2B) → closing CTA. Academy is presented as PART of the app
// (in-app cours + révisions + passeport), not as a Netflix product.
import { Link } from "react-router-dom";
import { ArrowRight, GraduationCap, Frame, Map, ExternalLink } from "lucide-react";
import Layout from "@/components/Layout";
import SEO from "@/components/SEO";
import { useLang } from "@/lib/i18n";
import AnimatedHero from "@/components/AnimatedHero";
import PillarsSchema from "@/components/PillarsSchema";
import AppExperienceBlock from "@/components/AppExperienceBlock";
import AudiencesBlock from "@/components/AudiencesBlock";
import CarteDuMoisBlock from "@/components/CarteDuMoisBlock";
import WhatAreYouLookingForBlock from "@/components/WhatAreYouLookingForBlock";
import PaperMapsMarquee from "@/components/PaperMapsMarquee";
import {
  geovinumOrganizationJsonLd,
  geovinumWebSiteJsonLd,
  geovinumWebApplicationJsonLd,
  faqPageJsonLd,
} from "@/components/SEO";

const WEB_APP_URL = "https://app.geovinum.io";

// Top 5 FAQ items surfaced in the homepage JSON-LD. Kept short so the
// homepage schema is scannable by LLMs — the full 20-question list
// lives on /faq with its own FAQPage schema.
const HOME_FAQ = [
  {
    q: "Qu'est-ce que geoVINUM ?",
    a: "geoVINUM est une plateforme de cartographie viticole, de données et d'apprentissage du vin. Cinq produits : Maps (cartes), Explorer (fiches d'appellations, cépages, producteurs), Academy (parcours WSET), AI (assistant conversationnel spécialisé vin) et Interactive / Data (cartes intégrables B2B).",
  },
  {
    q: "Où trouver une carte des vignobles du monde ?",
    a: "Sur geoVINUM, la Boutique regroupe les cartes viticoles des principales régions du monde (Bourgogne, Bordeaux, Rhône, Champagne, Italie, Espagne, Portugal, etc.) en version imprimée et téléchargeable.",
  },
  {
    q: "Comment apprendre la géographie du vin ?",
    a: "geoVINUM Academy propose des parcours structurés par région et par référentiel WSET (Levels 3, 4, 5). Chaque parcours combine cartes Maps, fiches Explorer et vidéos.",
  },
  {
    q: "Qu'est-ce que geoVINUM Explorer ?",
    a: "Explorer est la base éditoriale et exploratoire de geoVINUM : plus de 4 700 fiches (appellations, cépages, producteurs, articles pédagogiques) reliées entre elles par un graphe géographique.",
  },
  {
    q: "geoVINUM propose-t-il une cartographie interactive ?",
    a: "Oui, via geoVINUM Interactive et l'iframe intégrable pour les partenaires B2B (écoles, domaines, médias).",
  },
];

// Section-level translations (inline dict — the shared `i18n.jsx` file
// carries a lot of legacy Home keys that no longer match the current
// copy, so we keep the Home strings colocated with the sections that
// render them for clarity + easy future edits).
const HOME_I18N = {
  fr: {
    maps: {
      eyebrow: "geoVINUM Maps · le socle",
      title_a: "Comprendre le vin",
      title_b: "en le voyant.",
      body: "Les cartes sont le socle historique de geoVINUM. Régions viticoles, appellations et terroirs — en version physique comme numérique. Visualiser un vignoble, situer une appellation, comprendre un climat, sans avoir à décoder le jargon.",
      f1: "Visualiser les terroirs",
      f2: "Comprendre les appellations",
      f3: "Situer les régions",
      f4: "Apprendre visuellement",
      cta: "Explorer les cartes",
      shop: "Voir la boutique",
    },
    academy: {
      eyebrow: "geoVINUM Academy · dans l'app",
      title_a: "Apprendre le vin,",
      title_b: "quelques minutes par jour",
      body: "L'Academy fait partie intégrante de l'application. On y retrouve <b>Révise</b>, <b>Cours</b> et <b>Passeport</b> — micro-learning, flashcards, contenus pédagogiques sur les régions, les cépages et la culture du vin. Progresse à ton rythme, l'IA te guide.",
      f1: "Cours structurés",
      f2: "Flashcards espacées",
      f3: "Révisions quotidiennes",
      f4: "Passeport de progression",
      cta: "Ouvrir l'app",
      cta_extracts: "Voir des extraits",
    },
    iframe: {
      eyebrow: "geoVINUM iframe · pour les pros",
      title_a: "Ajoutez l'intelligence géographique du vin", title_b: "directement dans votre site",
      body: "Reliez un champ « appellation » à une carte interactive geoVINUM — automatiquement, sans dessiner chaque carte à la main. Idéal pour producteurs, cavistes, écoles du vin, interprofessions et sites œnotouristiques.",
      cta: "Voir les offres pros", contact: "Parler à l'équipe",
    },
    closing: {
      eyebrow: "Prêt à commencer ?",
      title_a: "Le vin,", title_b: "enfin lisible",
      sub: "Une carte pour voir · une Academy pour apprendre · une IA pour comprendre · un carnet pour déguster.",
      open_app: "Ouvrir l'app", for_pros: "Pour les pros",
    },
  },
  en: {
    maps: {
      eyebrow: "geoVINUM Maps · the foundation",
      title_a: "Understand wine",
      title_b: "by seeing it.",
      body: "Maps are the historical foundation of geoVINUM. Wine regions, appellations and terroirs — both physical and digital. Visualize a vineyard, locate an appellation, grasp a climate, without deciphering jargon.",
      f1: "Visualize terroirs",
      f2: "Understand appellations",
      f3: "Locate regions",
      f4: "Learn visually",
      cta: "Explore the maps",
      shop: "Browse the shop",
    },
    academy: {
      eyebrow: "geoVINUM Academy · in the app",
      title_a: "Learn wine,",
      title_b: "a few minutes a day",
      body: "The Academy is a core part of the app. You'll find <b>Review</b>, <b>Courses</b> and <b>Passport</b> — micro-learning, flashcards, educational content on regions, grape varieties and wine culture. Progress at your own pace, guided by AI.",
      f1: "Structured courses",
      f2: "Spaced flashcards",
      f3: "Daily reviews",
      f4: "Progression passport",
      cta: "Open the app",
      cta_extracts: "See excerpts",
    },
    iframe: {
      eyebrow: "geoVINUM iframe · for pros",
      title_a: "Bring wine geographic intelligence", title_b: "straight into your site",
      body: "Link an \u201cappellation\u201d field to an interactive geoVINUM map — automatically, without drawing every map by hand. Ideal for producers, wine merchants, wine schools, trade associations and wine-tourism sites.",
      cta: "See pro offers", contact: "Talk to the team",
    },
    closing: {
      eyebrow: "Ready to begin?",
      title_a: "Wine,", title_b: "finally readable",
      sub: "A map to see · an Academy to learn · an AI to understand · a journal to taste.",
      open_app: "Open the app", for_pros: "For pros",
    },
  },
  es: {
    maps: {
      eyebrow: "geoVINUM Maps · la base",
      title_a: "Comprender el vino",
      title_b: "viéndolo.",
      body: "Los mapas son la base histórica de geoVINUM. Regiones vitivinícolas, denominaciones y terruños — en versión física y digital. Visualiza un viñedo, ubica una denominación, comprende un clima, sin descifrar la jerga.",
      f1: "Visualizar terruños",
      f2: "Comprender denominaciones",
      f3: "Ubicar regiones",
      f4: "Aprender visualmente",
      cta: "Explorar los mapas",
      shop: "Ver la tienda",
    },
    academy: {
      eyebrow: "geoVINUM Academy · en la app",
      title_a: "Aprender vino,",
      title_b: "unos minutos al día",
      body: "La Academy forma parte integral de la aplicación. Encontrarás <b>Repasa</b>, <b>Cursos</b> y <b>Pasaporte</b> — microaprendizaje, flashcards, contenidos pedagógicos sobre regiones, variedades y cultura del vino. Avanza a tu ritmo, guiado por la IA.",
      f1: "Cursos estructurados",
      f2: "Flashcards espaciadas",
      f3: "Repasos diarios",
      f4: "Pasaporte de progreso",
      cta: "Abrir la app",
      cta_extracts: "Ver extractos",
    },
    iframe: {
      eyebrow: "geoVINUM iframe · para los pros",
      title_a: "Añade la inteligencia geográfica del vino", title_b: "directamente en tu sitio",
      body: "Vincula un campo «denominación» a un mapa interactivo geoVINUM — automáticamente, sin dibujar cada mapa a mano. Ideal para productores, tiendas de vino, escuelas, interprofesiones y sitios enoturísticos.",
      cta: "Ver las ofertas pro", contact: "Hablar con el equipo",
    },
    closing: {
      eyebrow: "¿Listos para empezar?",
      title_a: "El vino,", title_b: "por fin legible",
      sub: "Un mapa para ver · una Academy para aprender · una IA para comprender · un cuaderno para catar.",
      open_app: "Abrir la app", for_pros: "Para los pros",
    },
  },
  pt: {
    maps: {
      eyebrow: "geoVINUM Maps · a base",
      title_a: "Compreender o vinho",
      title_b: "ao vê-lo.",
      body: "Os mapas são a base histórica da geoVINUM. Regiões vinícolas, denominações e terroirs — em versão física e digital. Visualizar um vinhedo, localizar uma denominação, compreender um clima, sem decifrar jargão.",
      f1: "Visualizar terroirs",
      f2: "Compreender denominações",
      f3: "Localizar regiões",
      f4: "Aprender visualmente",
      cta: "Explorar os mapas",
      shop: "Ver a loja",
    },
    academy: {
      eyebrow: "geoVINUM Academy · na app",
      title_a: "Aprender vinho,",
      title_b: "alguns minutos por dia",
      body: "A Academy faz parte integrante da aplicação. Encontrarás <b>Revê</b>, <b>Cursos</b> e <b>Passaporte</b> — microaprendizagem, flashcards, conteúdos pedagógicos sobre regiões, castas e cultura do vinho. Avança ao teu ritmo, guiado pela IA.",
      f1: "Cursos estruturados",
      f2: "Flashcards espaçadas",
      f3: "Revisões diárias",
      f4: "Passaporte de progresso",
      cta: "Abrir a app",
    },
    iframe: {
      eyebrow: "geoVINUM iframe · para os pros",
      title_a: "Adicione a inteligência geográfica do vinho", title_b: "diretamente no seu site",
      body: "Ligue um campo \u00abdenomina\u00e7\u00e3o\u00bb a um mapa interativo geoVINUM \u2014 automaticamente, sem desenhar cada mapa \u00e0 m\u00e3o. Ideal para produtores, garrafeiras, escolas de vinho, interprofissões e sites de enoturismo.",
      cta: "Ver as ofertas pro", contact: "Falar com a equipa",
    },
    closing: {
      eyebrow: "Pronto para começar?",
      title_a: "O vinho,", title_b: "finalmente legível",
      sub: "Um mapa para ver · uma Academy para aprender · uma IA para compreender · um caderno para provar.",
      open_app: "Abrir a app", for_pros: "Para os pros",
    },
  },
};

const Home = () => {
  const lang = useLang();
  const t = HOME_I18N[lang] || HOME_I18N.fr;
  return (
    <Layout testId="page-home">
      <SEO
        title="geoVINUM — Cartographie, données et découverte des vignobles du monde"
        description="Plateforme spécialisée dans la cartographie viticole, les appellations, les terroirs et l'apprentissage du vin. Cartes physiques, Explorer, Academy WSET, AI et cartes interactives B2B."
        jsonLdList={[
          geovinumOrganizationJsonLd(),
          geovinumWebSiteJsonLd(),
          geovinumWebApplicationJsonLd(),
          faqPageJsonLd(HOME_FAQ),
        ]}
      />

      {/* SEO/GEO H1 — sémantique et factuel, non-visible (le hero animé
          fait office de titre visuel). Les LLMs et Googlebot lisent le
          H1 sans le rendu ; les utilisateurs voient le AnimatedHero. */}
      <h1 className="sr-only">
        geoVINUM — Cartographie, données et découverte des vignobles du monde
      </h1>

      <AnimatedHero />

      {/* 4+1 pillar schema — the visual anchor of the new narrative */}
      <PillarsSchema />

      {/* MAPS — the historic pillar: understand wine by seeing it */}
      <section className="py-24 md:py-32 bg-white overflow-hidden" data-testid="home-section-maps">
        <div className="max-w-7xl mx-auto px-6 lg:px-10 grid grid-cols-1 lg:grid-cols-12 gap-12 items-center">
          <div className="lg:col-span-5 reveal">
            <div className="inline-flex items-center gap-2 text-[11px] uppercase tracking-widest text-[color:var(--gv-pink)] mb-5">
              <Map size={13} /> {t.maps.eyebrow}
            </div>
            <h2 className="font-display text-[34px] md:text-[48px] leading-[1.08] text-[color:var(--gv-ink)] mb-5">
              {t.maps.title_a} <span className="italic text-[color:var(--gv-pink)]">{t.maps.title_b}</span>
            </h2>
            <p className="text-[16px] text-[color:var(--gv-ink)]/70 mb-6 max-w-lg">
              {t.maps.body}
            </p>
            <ul className="grid grid-cols-2 gap-x-6 gap-y-2 text-[14px] text-[color:var(--gv-ink)]/85 mb-8 max-w-md">
              <li className="flex items-center gap-2"><span className="w-1 h-1 rounded-full bg-[color:var(--gv-pink)]" /> {t.maps.f1}</li>
              <li className="flex items-center gap-2"><span className="w-1 h-1 rounded-full bg-[color:var(--gv-pink)]" /> {t.maps.f2}</li>
              <li className="flex items-center gap-2"><span className="w-1 h-1 rounded-full bg-[color:var(--gv-pink)]" /> {t.maps.f3}</li>
              <li className="flex items-center gap-2"><span className="w-1 h-1 rounded-full bg-[color:var(--gv-pink)]" /> {t.maps.f4}</li>
            </ul>
            <div className="flex flex-col sm:flex-row gap-3">
              <Link to="/cartes" className="btn-primary" data-testid="home-maps-cta">
                {t.maps.cta} <ArrowRight size={16} />
              </Link>
              <Link to="/shop" className="btn-secondary" data-testid="home-maps-shop">
                {t.maps.shop} <ArrowRight size={14} />
              </Link>
            </div>
          </div>
          {/* Paper-maps marquee — two rows of Shop products auto-scrolling in
              opposite directions. Each poster links to its PDP. Replaces the
              previous static iPhone image so the "socle physique" claim is
              visually true. */}
          <div className="lg:col-span-7 reveal">
            <PaperMapsMarquee />
          </div>
        </div>
      </section>

      {/* AI = THE APP — 5 usages showcase */}
      <AppExperienceBlock />

      {/* Editorial rendezvous — the "Carte du mois" rendered only when the
          admin has published one via /admin/shop/carte-du-mois. */}
      <CarteDuMoisBlock />

      {/* Auto-segmentation — 4 entry points that route to a filtered /shop. */}
      <WhatAreYouLookingForBlock />

      {/* ACADEMY — presented as an in-app experience (not Netflix) */}
      <section className="py-24 md:py-32 bg-[color:var(--gv-mist)]" data-testid="home-section-academy">
        <div className="max-w-7xl mx-auto px-6 lg:px-10 grid grid-cols-1 lg:grid-cols-12 gap-12 items-center">
          <div className="lg:col-span-6 reveal order-2 lg:order-1">
            {/* Laptop mockup — pure CSS "MacBook"-style frame: dark bezel with
                a webcam dot, screen inset, and a wedge base with a hinge
                slot. Wraps the app screenshot so the Academy section reads
                as an *in-computer* experience. */}
            <div className="relative mx-auto max-w-[640px]" data-testid="academy-laptop">
              {/* Screen bezel */}
              <div className="relative bg-[#1c1c1c] rounded-t-[14px] p-[10px] pb-[8px] shadow-[0_30px_80px_-30px_rgba(46,80,103,0.45)]">
                {/* Camera dot */}
                <div className="absolute top-[4px] left-1/2 -translate-x-1/2 w-1.5 h-1.5 rounded-full bg-[#333]" aria-hidden />
                <div className="rounded-[6px] overflow-hidden bg-white aspect-[16/10] flex items-center justify-center">
                  <img
                    src="https://customer-assets-7cd3h4nn.emergentagent.net/job_geo-vinum-preview/artifacts/v8m6oz1r_Captura%20de%20Tela%202026-09-02%20a%CC%80s%2022.20.09.webp"
                    alt="geoVINUM Academy — Study Schedule dans l'app"
                    loading="lazy"
                    className="max-w-full max-h-full w-auto h-auto object-contain"
                    data-testid="home-academy-laptop-img"
                  />
                </div>
              </div>
              {/* Base wedge */}
              <div className="relative">
                <div className="h-[14px] bg-gradient-to-b from-[#c9ced3] to-[#8b9298] rounded-b-[3px]" />
                {/* Hinge notch */}
                <div className="absolute top-0 left-1/2 -translate-x-1/2 w-[80px] h-[6px] bg-[#7c848b] rounded-b-[6px]" aria-hidden />
                {/* Ground shadow */}
                <div className="mx-auto mt-2 h-4 w-4/5 bg-gradient-to-b from-black/15 to-transparent blur-md rounded-full" aria-hidden />
              </div>
            </div>
          </div>
          <div className="lg:col-span-6 reveal order-1 lg:order-2">
            <div className="inline-flex items-center gap-2 text-[11px] uppercase tracking-widest text-[color:var(--gv-pink)] mb-5">
              <GraduationCap size={13} /> {t.academy.eyebrow}
            </div>
            <h2 className="font-display text-[34px] md:text-[48px] leading-[1.08] text-[color:var(--gv-ink)] mb-5">
              {t.academy.title_a} <span className="italic text-[color:var(--gv-pink)]">{t.academy.title_b}</span>.
            </h2>
            <p
              className="text-[16px] text-[color:var(--gv-ink)]/70 mb-6 max-w-lg"
              dangerouslySetInnerHTML={{ __html: t.academy.body }}
            />
            <ul className="grid grid-cols-2 gap-x-6 gap-y-2 text-[14px] text-[color:var(--gv-ink)]/85 mb-8 max-w-md">
              <li className="flex items-center gap-2"><span className="w-1 h-1 rounded-full bg-[color:var(--gv-pink)]" /> {t.academy.f1}</li>
              <li className="flex items-center gap-2"><span className="w-1 h-1 rounded-full bg-[color:var(--gv-pink)]" /> {t.academy.f3}</li>
              <li className="flex items-center gap-2"><span className="w-1 h-1 rounded-full bg-[color:var(--gv-pink)]" /> {t.academy.f2}</li>
              <li className="flex items-center gap-2"><span className="w-1 h-1 rounded-full bg-[color:var(--gv-pink)]" /> {t.academy.f4}</li>
            </ul>
            <div className="flex flex-col sm:flex-row gap-3">
              <a href={`${WEB_APP_URL}/academy`} target="_blank" rel="noreferrer" className="btn-primary" data-testid="home-academy-app-cta">
                {t.academy.cta} <ExternalLink size={14} />
              </a>
              <Link to="/academy" className="btn-secondary" data-testid="home-academy-extracts">
                {t.academy.cta_extracts} <ArrowRight size={14} />
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* Two audiences */}
      <AudiencesBlock />

      {/* IFRAME (B2B) — repositioned as "geoVINUM intelligence into your site" */}
      <section className="py-24 md:py-32 bg-[color:var(--gv-ink)] text-white" data-testid="home-section-iframe">
        <div className="max-w-7xl mx-auto px-6 lg:px-10 grid grid-cols-1 lg:grid-cols-12 gap-12 items-center">
          <div className="lg:col-span-6 reveal">
            <div className="inline-flex items-center gap-2 text-[11px] uppercase tracking-widest text-[color:var(--gv-pink)] mb-5">
              <Frame size={13} /> {t.iframe.eyebrow}
            </div>
            <h2 className="font-display text-[34px] md:text-[48px] leading-[1.08] text-white mb-5">
              {t.iframe.title_a} <span className="italic text-[color:var(--gv-pink)]">{t.iframe.title_b}</span>.
            </h2>
            <p className="text-[16px] text-white/70 mb-7 max-w-lg">
              {t.iframe.body}
            </p>
            <div className="flex flex-wrap gap-2 mb-8 text-[12px] text-white/85">
              <span className="px-3 py-1 rounded-full border border-white/15">Iframe Mini</span>
              <span className="px-3 py-1 rounded-full border border-white/15">Magnum</span>
              <span className="px-3 py-1 rounded-full border border-white/15">Jeroboam</span>
              <span className="px-3 py-1 rounded-full border border-white/15">Enterprise</span>
            </div>
            <div className="flex flex-col sm:flex-row gap-3">
              <Link to="/pro" className="btn-primary" data-testid="home-iframe-cta">
                {t.iframe.cta} <ArrowRight size={16} />
              </Link>
              <Link
                to="/contact?topic=iframe-enterprise"
                className="text-white/85 inline-flex items-center gap-2 text-[14px] hover:text-[color:var(--gv-pink)] px-4 py-2.5"
                data-testid="home-iframe-sales"
              >
                {t.iframe.contact} <ArrowRight size={14} />
              </Link>
            </div>
          </div>
          <div className="lg:col-span-6 reveal">
            <div className="rounded-[6px] overflow-hidden shadow-[0_30px_80px_-30px_rgba(46,80,103,0.35)]">
              <img
                src="https://customer-assets.emergentagent.com/job_geo-vinum-preview/artifacts/3m7qbj61_geovinumio-iframe.jpg"
                alt="geoVINUM iframe intégration"
                loading="lazy"
                className="w-full h-[420px] md:h-[500px] object-cover"
              />
            </div>
          </div>
        </div>
      </section>

      {/* Closing CTA */}
      <section className="py-20 bg-[color:var(--gv-mist)]" data-testid="home-section-closing">
        <div className="max-w-4xl mx-auto px-6 lg:px-10 text-center">
          <div className="text-[11px] uppercase tracking-widest text-[color:var(--gv-pink)] mb-4">{t.closing.eyebrow}</div>
          <h2 className="font-display text-[32px] md:text-[42px] leading-tight text-[color:var(--gv-ink)] mb-4">
            {t.closing.title_a} <span className="italic text-[color:var(--gv-pink)]">{t.closing.title_b}</span>.
          </h2>
          <p className="text-[15.5px] text-[color:var(--gv-ink)]/70 mb-8 max-w-xl mx-auto">
            {t.closing.sub}
          </p>
          <div className="flex flex-wrap justify-center gap-3">
            <a href={WEB_APP_URL} target="_blank" rel="noreferrer" className="btn-primary" data-testid="home-closing-webapp">
              {t.closing.open_app} <ExternalLink size={16} />
            </a>
            <Link to="/pro" className="btn-secondary" data-testid="home-closing-pro">
              {t.closing.for_pros} <ArrowRight size={14} />
            </Link>
          </div>
        </div>
      </section>
    </Layout>
  );
};

export default Home;
